import React, { FC, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import { Alert } from '@cmsgov/ds-medicare-gov'
import { MsnSettingsForm, createSchema } from 'models/forms/MsnSettingsForm'
import { MyDialog } from 'components/MyDialog'
import { MyChoiceList } from 'components/ReactHookForm/MyChoiceList'
import { Button } from 'components/Button/Button'
import { useDispatch, useSelector } from 'react-redux'
import { medicareApiClient } from 'clients/medicareApiClient'
import { getUserData, updateUser } from 'redux/reducers/users'
import { shallowDiff } from 'utils/object-helpers'
import { MsnSettingsModalUneditable } from './MsnSettingsModalUneditable'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { useModalCloseEvent } from 'components/MyDialog/useModalCloseEvent'
import { Component, EMsnMedia, IEMsnPreferences } from '@cms-max/medicare-api-sdk'
import { MySpinner } from 'components/MySpinner/MySpinner'

export type MsnSettingsServerErrors = {
  basic?: boolean,
  emailFail?: boolean,
  emailSuccessPrefFail?: boolean,
}

export type MsnSettingsModalProps = {
  onClose: () => void,
  onSubmit: (formData: MsnSettingsForm) => void,
  loading?: boolean
  existingValues: IEMsnPreferences,
  existingEmail: string
  serverErrors?: MsnSettingsServerErrors
}

export const MsnSettingsModal: FC<MsnSettingsModalProps> = ({ onClose, onSubmit, loading = false, serverErrors = {}, existingValues, existingEmail }) => {
  const { t } = useTranslation(['editMsnSettings', 'commonEmailErrors'])

  const hasCustomFormat = existingValues?.media === EMsnMedia.CustomFormat

  // Unlike the handbook settings, we are simply setting a boolean for electronic format preference
  const formatChoices = [
    {
      label: t('radio-format.opts.electronic'),
      value: 'true'
    },
    {
      label: hasCustomFormat ? (existingValues.customMediaDescription ?? EMsnMedia.CustomFormat) : existingValues.media,
      value: 'false'
    }
  ]

  const languageChoices = [
    {
      label: t('radio-language.opts.en'),
      value: 'en'
    },
    {
      label: t('radio-language.opts.es'),
      value: 'es'
    }
  ]

  const defaultValues: Partial<MsnSettingsForm> = {
    isEmailPreferred: String(existingValues.isEmailPreferred ?? 'false'),
    language: existingValues.language as 'en' | 'es',
    email: '',
    confirmEmail: ''
  }

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: zodResolver(schema)
  })

  const { formState, watch, setValue, clearErrors } = methods
  const watchFormat = watch('isEmailPreferred')

  const [emailsReceivedErrors, setEmailsReceivedErrors] = useState(false)
  const formErrors = methods.formState.errors
  useEffect(() => {
    if (formErrors.confirmEmail || formErrors.email) {
      setEmailsReceivedErrors(true)
    }
  }, [formErrors.email, formErrors.confirmEmail, setEmailsReceivedErrors])

  useEffect(() => {
    if (watchFormat === 'true') {
      const setValueConfig = {
        shouldValidate: Boolean(existingEmail)
      }
      setEmailsReceivedErrors(false)
      setValue('email', existingEmail ?? '', setValueConfig)
      setValue('confirmEmail', existingEmail ?? '', setValueConfig)
    } else if (watchFormat === 'false') {
      setValue('email', '')
      setValue('confirmEmail', '')
      clearErrors(['email', 'confirmEmail'])
      setEmailsReceivedErrors(false)
    }
  }, [watchFormat, existingEmail, setValue, clearErrors, setEmailsReceivedErrors])

  const handleClose = useModalCloseEvent('accountSettings', 'accountsettings_emsnclose_click', onClose)

  return (
    <MyDialog
      heading={t('heading')}
      containerId='medicare-my-acct-msn-settings'
      closeButtonAriaLabel='Close'
      onClose={handleClose}
      >
        <>
          <p className='my-u-pb--8'>{t('subtitle-paragraph')}</p>
          <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="my-u-mb--14">
              <MyChoiceList
                variableInputName="isEmailPreferred"
                label={<h2 className="ds-text-heading--xl">{t('radio-format.label')}</h2>}
                errorMessageString=""
                choices={formatChoices}
              />
              <MyChoiceList
                variableInputName="language"
                label={<h2 className="ds-text-heading--xl">{t('radio-language.label')}</h2>}
                errorMessageString=""
                choices={languageChoices}
              />
              {/* Display errors if email somehow has an error so the user can fix it */}
              {(!existingEmail && watchFormat === 'true') || emailsReceivedErrors
                ? (
                <fieldset className="ds-c-fieldset">
                  <legend className="ds-c-label"><h2 className='ds-text-heading--xl'>{t(`email-add.heading`)}</h2></legend>
                  <p>{t('email-add.description')}</p>
                  <MyTextField
                    name='email'
                    label={t(`email-add.label1`)}
                    labelClassName='my-u-text-transform--default'
                    defaultValue=''
                    addErrorPrefix
                    onBlur={() => methods.trigger(['email', 'confirmEmail'])}
                  />
                  <MyTextField
                    name='confirmEmail'
                    label={t(`email-add.label2`)}
                    labelClassName='my-u-text-transform--default'
                    defaultValue=''
                    addErrorPrefix
                    onBlur={() => methods.trigger(['email', 'confirmEmail'])}
                  />
              </fieldset>
                  )
                : null}
            </div>

            {serverErrors?.basic
              ? (
              <Alert variation="error" className="my-u-mb--14">
                <p className="ds-c-alert__text">{t('errors.server-basic')}</p>
              </Alert>
                )
              : null}
            {serverErrors?.emailFail
              ? (
              <Alert variation="error" className="my-u-mb--14">
                <p className="ds-c-alert__text">{t('errors.server-email-fail')}</p>
              </Alert>
                )
              : null}
            {serverErrors?.emailSuccessPrefFail
              ? (
              <Alert variation="error" className="my-u-mb--14">
                <p className="ds-c-alert__text">{t('errors.server-email-success-pref-fail')}</p>
              </Alert>
                )
              : null}

            <div className='ds-c-dialog__actions'>
              <div className='ds-l-form-row'>
                <div className='ds-l-col--12 ds-l-sm-col--auto my-u-my--2'>
                  <Button
                    type='submit'
                    className='ds-c-button ds-c-button--solid my-u-py--6 my-u-width--full'
                    disabled={isEmpty(formState.dirtyFields)}
                    siteActivityProps={{
                      core: {
                        component: Component.AccountSettings,
                        elementId: 'accountsettings_emsnsave_click',
                        coreClickData: {
                          linkText: t('submit')
                        }
                      }
                    }}
                  >
                  {loading && (
                    <MySpinner size='small' inverse isInButton/>
                  )}
                  {t('submit')}
                  </Button>
                </div>
                <div className='ds-l-col--12 ds-l-sm-col--auto my-u-my--2'>
                  <Button
                    className='ds-c-button my-u-py--6 my-u-width--full'
                    onClick={onClose}
                    siteActivityProps={{
                      core: {
                        component: Component.AccountSettings,
                        elementId: 'accountsettings_handbookclose_click',
                        coreClickData: {
                          linkText: t('cancel')
                        }
                      }
                    }}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            </div>
          </form>
          </FormProvider>
        </>
    </MyDialog>
  )
}

export type MsnSettingsProps = Pick<MsnSettingsModalProps, 'onClose'>
export const EditMsnSettings: React.FC<MsnSettingsProps> = ({ onClose }) => {
  const [loading, setLoading] = useState(false)
  const [serverError, setServerError] = useState<Partial<MsnSettingsServerErrors>>({})
  const dispatch = useDispatch()
  const userData = useSelector(getUserData)
  const existing = userData?.user?.eMsnPreferences

  if (!existing) {
    return <MsnSettingsModalUneditable onClose={onClose} />
  }

  const handleSubmit = async (formData: MsnSettingsForm) => {
    setLoading(true)
    setServerError({})

    const formattedFormData = {
      isEmailPreferred: formData?.isEmailPreferred === 'true',
      language: formData?.language
    }
    // Payload should include only updated values
    const payload = shallowDiff(existing, formattedFormData)

    const needUpdateEmail = Boolean(formData.email && userData.user?.email !== formData.email)

    if (formData.email && needUpdateEmail) {
      try {
        await medicareApiClient.asyncUpdateBeneficiaryEmail({
          email: formData.email
        })
        dispatch(updateUser({
          email: formData.email
        }))
      } catch (error) {
        console.error('Error updating email', error)
        setServerError({ emailFail: true })
        setLoading(false)
        // Do not continue
        return
      }
    }

    try {
      // API request needs to omit `media` value
      await medicareApiClient.asyncUpdateBeneficiary({ eMsnPreferences: payload })

      // Redux needs to maintain the `media` value
      dispatch(updateUser({ eMsnPreferences: { ...existing, ...payload } }))
      setLoading(false)

      // Close modal
      onClose?.()
    } catch (error) {
      if (formattedFormData?.isEmailPreferred && needUpdateEmail) {
        setServerError({ emailSuccessPrefFail: true })
      } else {
        setServerError({ basic: true })
      }

      setLoading(false)
    }
  }
  return (
    <MsnSettingsModal
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      serverErrors={serverError}
      existingValues={existing}
      existingEmail={userData.user?.email ?? ''}
    />
  )
}
